import { createFileRoute, useNavigate } from '@tanstack/react-router'
import React, { useEffect, useState, useCallback } from 'react'
import { PricingTable as ClerkPricingTable } from '@clerk/clerk-react'
import ImagePreviewCard from '../components/ImagePreviewCard'
import { CreditPurchaseCalculator } from '../components/CreditPurchaseCalculator'
import PurchaseCreditsModal from '../components/PurchaseCreditsModal'

// Type definitions for better type safety
interface PreviewFile {
  name: string;
  size: string;
  isImage: boolean;
  preview?: string;
  actualSize?: number;
}

interface LimitReason {
  type: 'count' | 'size';
  attempted: number;
  limit: number;
  message: string;
}

interface AnalysisResult {
  success: boolean;
  category?: string;
  description?: string;
  error?: string;
}

interface APIResult {
  filename: string;
  success: boolean;
  category: string;
  description: string;
  error?: string;
}

const PlansPage: React.FC = () => {
  const [previewData, setPreviewData] = useState<PreviewFile[] | null>(null)
  const [limitReason, setLimitReason] = useState<LimitReason | null>(null)
  const [analysisResults, setAnalysisResults] = useState<Record<string, AnalysisResult>>({})
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [showPurchaseModal, setShowPurchaseModal] = useState(false)
  const [purchaseData, setPurchaseData] = useState({ imageCount: 0, credits: 0, totalCost: 0 })
  const navigate = useNavigate()
  const search = Route.useSearch()

  // Format file size helper
  const formatFileSize = (bytes: number): string => {
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  // Submit preview job with progressive results (2025 best practice)
  const submitPreviewJobProgressive = useCallback(async (previews: PreviewFile[]) => {
    try {
      console.log('Submitting progressive preview job for', previews.length, 'images');

      // Validate file sizes before submission (10MB limit for preview endpoint)
      const MAX_PREVIEW_SIZE = 10 * 1024 * 1024; // 10MB
      const oversizedFiles: Array<{name: string; size: string}> = [];

      // Create FormData from base64 image data
      const formData = new FormData();

      for (const preview of previews) {
        if (preview.preview) {
          // Use stored actualSize if available, otherwise get blob size
          const {actualSize} = preview;
          if (actualSize && actualSize > MAX_PREVIEW_SIZE) {
            oversizedFiles.push({
              name: preview.name,
              size: formatFileSize(actualSize)
            });
            console.warn(`File ${preview.name} (${formatFileSize(actualSize)}) exceeds 10MB preview limit`);
            continue; // Skip oversized files
          }

          // Convert blob URL to blob for files under 10MB
          try {
            const response = await fetch(preview.preview);
            const blob = await response.blob();

            // Double-check blob size if actualSize wasn't available
            if (!actualSize && blob.size > MAX_PREVIEW_SIZE) {
              oversizedFiles.push({
                name: preview.name,
                size: formatFileSize(blob.size)
              });
              console.warn(`File ${preview.name} (${formatFileSize(blob.size)}) exceeds 10MB preview limit`);
              continue; // Skip oversized files
            }

            // Add to form data
            formData.append('images', blob, preview.name);
          } catch (error) {
            console.warn(`Failed to process preview for ${preview.name}:`, error);
          }
        }
      }

      // Show error message if files are too large for preview
      if (oversizedFiles.length > 0) {
        const errorMessage = oversizedFiles.length === previews.length
          ? 'All selected images are too large for preview (10MB limit). The full processing service will handle these files.'
          : `${oversizedFiles.length} of ${previews.length} images are too large for preview (10MB limit): ${oversizedFiles.map(f => `${f.name} (${f.size})`).join(', ')}. Processing will continue with smaller files.`;

        // Handle oversized files error
        if (oversizedFiles.length === previews.length) {
          setIsAnalyzing(false);
          console.log('All files too large for preview:', errorMessage);
          return; // Exit early if no files can be previewed
        }
      }

      // Continue with valid files if any exist
      if (formData.has('images')) {
        // Use Convex preview endpoint (HTTP routes use .convex.site)
        const convexUrl = process.env.REACT_APP_CONVEX_URL || 'https://upbeat-butterfly-447.convex.cloud';
        const convexSiteUrl = convexUrl.replace('.convex.cloud', '.convex.site');
        const apiResponse = await fetch(`${convexSiteUrl}/preview`, {
          method: 'POST',
          body: formData
        });

        if (apiResponse.ok) {
          const result = await apiResponse.json();
          console.log('Preview job completed:', result);

          // Simulate progressive rendering by staggering the results
          if (result.results && Array.isArray(result.results)) {
            result.results.forEach((resultItem: APIResult, index: number) => {
              setTimeout(() => {
                console.log(`Displaying result for slot ${index + 1}:`, resultItem);
                // FIX: Use previews parameter instead of stale previewData state
                // This ensures we always have the correct reference to the preview items
                const previewItem = previews[index];
                if (previewItem) {
                  console.log(`Updating analysis result for ${previewItem.name}:`, {
                    success: resultItem.success,
                    category: resultItem.category,
                    description: resultItem.description
                  });
                  setAnalysisResults(prev => ({
                    ...prev,
                    [previewItem.name]: {
                      success: resultItem.success,
                      category: resultItem.category,
                      description: resultItem.description,
                      error: resultItem.error
                    }
                  }));
                } else {
                  console.error(`No preview item found at index ${index}`);
                }

                // Stop analyzing when all results are displayed
                if (index === result.results.length - 1) {
                  setIsAnalyzing(false);
                  console.log('All analysis results displayed');
                }
              }, index * 800); // Stagger by 800ms to simulate progressive rendering
            });
          } else {
            setIsAnalyzing(false);
          }
        } else {
          console.error('Preview job failed:', await apiResponse.text());
          setIsAnalyzing(false);
        }

      } else {
        console.log('No valid files for preview after size validation');
        setIsAnalyzing(false);
      }

    } catch (error) {
      console.error('Error submitting progressive preview job:', error);
      setIsAnalyzing(false);
    }
  }, []); // Remove previewData from dependencies to avoid stale closures





  // Check if user came from limit error and show previews
  useEffect(() => {
    const previewData = sessionStorage.getItem('sortingcat_limit_previews');
    const limitReason = sessionStorage.getItem('sortingcat_limit_reason');
    
    if ((search?.from === 'limit' || search?.from === 'submit') && previewData && limitReason) {
      try {
        const previews = JSON.parse(previewData);
        const reason = JSON.parse(limitReason);
        
        // Validate preview data structure
        if (!Array.isArray(previews) || previews.length === 0) {
          throw new Error('Invalid preview data: must be non-empty array');
        }
        
        for (const preview of previews) {
          if (!preview || typeof preview !== 'object' || 
              typeof preview.name !== 'string' || 
              typeof preview.size !== 'string' || 
              typeof preview.isImage !== 'boolean') {
            throw new Error('Invalid preview item structure');
          }
        }
        
        // Validate reason data structure  
        if (!reason || typeof reason !== 'object' ||
            !['count', 'size'].includes(reason.type) ||
            typeof reason.attempted !== 'number' ||
            typeof reason.limit !== 'number' ||
            typeof reason.message !== 'string') {
          throw new Error('Invalid reason data structure');
        }
        
        setPreviewData(previews);
        setLimitReason(reason);
        
        // Start analysis and submit preview job
        setIsAnalyzing(true);
        setTimeout(() => {
          submitPreviewJobProgressive(previews);
        }, 1000);
        
        // Clear session storage after displaying
        sessionStorage.removeItem('sortingcat_limit_previews');
        sessionStorage.removeItem('sortingcat_limit_reason');
        
        console.log('Displayed limit previews:', previews.length, 'images');
      } catch (err) {
        console.warn('Failed to display limit previews:', err);
        // Clear potentially corrupted session storage data
        sessionStorage.removeItem('sortingcat_limit_previews');
        sessionStorage.removeItem('sortingcat_limit_reason');
      }
    }
  }, [search?.from, submitPreviewJobProgressive]);



  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <header className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src="/sorting_cat_mascot.png" alt="SortingCat Mascot" className="h-36 w-36 object-cover rounded-full border-2 border-purple-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-500 to-purple-700 bg-clip-text text-transparent mb-2">Almost sorted...</h1>
        </header>

        {/* Image Preview Section (shown when redirected from limits) */}
        {previewData && limitReason && (
          <div className="mb-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="text-center mb-4">
              </div>
              
              {/* Image Preview Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                {previewData.slice(0, 3).map((preview: PreviewFile, index: number) => (
                  <ImagePreviewCard
                    key={`${preview.name}-${index}`}
                    preview={preview}
                    analysisResult={analysisResults[preview.name]}
                    isAnalyzing={isAnalyzing}
                    index={index}
                  />
                ))}
              </div>
              
              <div className="text-center text-sm text-gray-500">
                Showing first 3 images from your selection
              </div>
            </div>
          </div>
        )}

        {/* Current Limitation Alert */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-red-600 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 18.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-lg font-medium text-red-800">Free Tier Limit Reached</h3>
              <p className="text-red-700 mt-1">
                You&apos;ve selected more than 10 images or exceeded 50mb total file size.
              </p>
            </div>
          </div>
        </div>

        {/* Credit Purchase Calculator - shown when from=limit */}
        {search?.from === 'limit' && (
          <div className="mb-8">
            <CreditPurchaseCalculator
              autoExpanded={true}
              initialImageCount={search?.imageCount ? parseInt(String(search.imageCount), 10) : undefined}
              onPurchaseClick={(imageCount, credits, totalCost) => {
                setPurchaseData({ imageCount, credits, totalCost });
                setShowPurchaseModal(true);
              }}
            />
          </div>
        )}

        {/* Subscription Plans Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-center mb-6 bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
            Or subscribe for big savings!
          </h2>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <ClerkPricingTable />
          </div>
        </div>

        {/* Back Button */}
        <div className="text-center">
          <button
            onClick={() => {
              navigate({ to: '/', search: { jobId: undefined } });
            }}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md font-medium hover:bg-gray-200 transition-colors inline-flex items-center cursor-pointer"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
            </svg>
            Back to SortingCat
          </button>
        </div>
      </div>

      {/* Purchase Credits Modal */}
      <PurchaseCreditsModal
        isOpen={showPurchaseModal}
        fileCount={purchaseData.imageCount}
        onClose={() => setShowPurchaseModal(false)}
      />
    </div>
  );
};

export const Route = createFileRoute('/plans')({
  component: PlansPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      from: search.from as string | undefined,
      imageCount: search.imageCount as number | undefined,
      selected_plan: search.selected_plan as string | undefined,
    }
  },
})